import { GlassCard } from '@/components/ui/GlassCard';
import { Badge } from '@/components/ui/Badge';
import { TrendChart, type TrendPoint } from '@/components/charts/TrendChart';
import { kpiDef } from '@/lib/kpi-definitions';
import { drawnPoints } from '@/lib/aggregate';
import type { KgiMetrics } from '@/lib/api';

interface KgiHeroProps {
  kgi: KgiMetrics | null;
  month: string | null;
  /** 退会率の推移（古い順・選択期間で集約済み） */
  trend: TrendPoint[];
  /** X軸ラベル（'期間（月次）' 等） */
  xLabel: string;
  /** Y軸に添える集約方法（'四半期平均' 等）。月次では null */
  aggregation: string | null;
  /** 増減の比較先ラベル（'前月比' / '前四半期比' / '前年度比'） */
  comparisonLabel: string;
  isLoading?: boolean;
}

/** @graph-connects none */
function formatPercent(value: number | null, digits = 1): string {
  if (value == null) return '—';
  return (value * 100).toFixed(digits);
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business QAGグループのKGI「退会率(ladies)」を最上部にヒーロー表示する。分子・分母の内訳と月次推移を併記
 * @graph-connects TrendChart [calls] 退会率の推移を軸付きで描画
 */
export function KgiHero({
  kgi,
  month,
  trend,
  xLabel,
  aggregation,
  comparisonLabel,
  isLoading,
}: KgiHeroProps) {
  if (isLoading) {
    return (
      <GlassCard elevated className="p-6 mb-6">
        <div className="skeleton-shimmer h-3 w-40 rounded mb-4" />
        <div className="skeleton-shimmer h-12 w-48 rounded mb-3" />
        <div className="skeleton-shimmer h-14 w-full rounded" />
      </GlassCard>
    );
  }

  // 見出しの値も増減も、**表示中の系列（trend）から取る**。
  // 2026-10-08 まで見出しだけ `kgi.churnRate`（期間集約と無関係な最新「月」のスナップショット）を
  // 読んでいたため、(a) 期間トグルを切り替えても見出しの数字が動かず、
  // (b) 四半期を選ぶと「単月の率」に「前四半期比」が付く、という食い違いが起きていた。
  // 系列の末尾から両方取れば、カードとグラフが構造的にずれない。
  // 値のある点だけを見る。素の末尾を読むと、末尾が null で埋まる期間（月次）で
  // 線は出ているのに見出しだけ — になり、増減も null 同士の引き算で消える
  const drawn = drawnPoints(trend);
  const latest = drawn.length >= 1 ? drawn[drawn.length - 1].value : null;
  const prev = drawn.length >= 2 ? drawn[drawn.length - 2].value : null;
  const periodLabel = drawn.length >= 1 ? drawn[drawn.length - 1].period : null;
  const diffPt = latest != null && prev != null ? (latest - prev) * 100 : null;
  // 退会率は低いほど良い
  const isImproving = diffPt != null && diffPt < 0;

  return (
    <GlassCard elevated padding="none" className="mb-6 overflow-hidden">
      <div className="p-6">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span
            className="text-xs font-semibold tracking-wide"
            style={{ color: 'var(--color-primary)' }}
          >
            QAG グループ KGI
          </span>
          <Badge variant="default" size="sm">
            責任者: ana
          </Badge>
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            {periodLabel ?? month ?? '—'} / ladies
          </span>
        </div>

        <p className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
          {kpiDef('churnRate').label}
        </p>

        <div className="flex items-baseline gap-3 flex-wrap">
          <span
            className="text-5xl font-bold tabular-nums leading-none"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {formatPercent(latest)}
          </span>
          <span className="text-xl" style={{ color: 'var(--color-text-muted)' }}>
            %
          </span>
          {diffPt != null && (
            <span
              className="text-sm font-medium tabular-nums"
              style={{ color: isImproving ? 'var(--color-success)' : 'var(--color-error)' }}
            >
              {isImproving ? '↓' : '↑'} {Math.abs(diffPt).toFixed(1)}pt {comparisonLabel}
            </span>
          )}
        </div>

        {/* 内訳は API が月単位でしか配信していない。見出しが四半期・年度のときに
            「その期間の内訳」と取り違えられないよう、どの月の数字かを明示する */}
        <div
          className="mt-4 flex gap-6 flex-wrap text-xs"
          style={{ color: 'var(--color-text-muted)' }}
        >
          {month != null && <span>内訳（{month}）</span>}
          <span>
            月額退会{' '}
            <strong className="tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
              {kgi?.churnCountMonthly?.toLocaleString() ?? '—'}
            </strong>{' '}
            件
          </span>
          <span>
            ダブルレンタル{' '}
            <strong className="tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
              {kgi?.churnCountDoubleRental?.toLocaleString() ?? '—'}
            </strong>{' '}
            件
          </span>
          <span>
            月額有料在籍{' '}
            <strong className="tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
              {kgi?.activeMembersBase?.toLocaleString() ?? '—'}
            </strong>{' '}
            名
          </span>
          {kpiDef('churnRate').baseline && <span>{kpiDef('churnRate').baseline}</span>}
        </div>
      </div>

      {trend.length > 0 && (
        <div className="px-3 pb-3">
          <TrendChart
            data={trend}
            yLabel={kpiDef('churnRate').metricName}
            xLabel={xLabel}
            aggregation={aggregation}
            unit="percent"
            color="var(--color-primary)"
            height={220}
          />
        </div>
      )}
    </GlassCard>
  );
}
